import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, Save, Eye, Plus, Trash2, Loader2, Home } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ImageUploader } from "@/components/admin/ImageUploader";
import { AdminProducts } from "@/components/admin/AdminProducts";
import { AdminVideos } from "@/components/admin/AdminVideos";

const TABS = ["Business", "Hero", "Collections", "Products", "Videos", "Why Us", "Marquee"];

const inputCls = "w-full bg-black border border-white/15 px-4 py-3 text-white placeholder:text-white/30 focus:border-ad-red focus:outline-none transition-colors";
const labelCls = "block text-xs font-bold uppercase tracking-widest text-white/50 mb-2";

const Field = ({ label, children }) => (
  <div>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [tab, setTab] = useState("Business");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  useEffect(() => {
    api.get("/content").then((res) => setContent(res.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", content);
      toast.success("Changes saved & published!");
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!content || user === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-ad-black">
        <Loader2 className="animate-spin text-ad-red" size={32} />
      </div>
    );
  }

  // helpers
  const setBiz = (k, v) => setContent({ ...content, business: { ...content.business, [k]: v } });
  const setHero = (k, v) => setContent({ ...content, hero: { ...content.hero, [k]: v } });
  const setAbout = (k, v) => setContent({ ...content, about: { ...content.about, [k]: v } });

  const setPhone = (i, v) => {
    const phones = [...(content.business.phones || [])];
    phones[i] = v;
    setBiz("phones", phones);
  };
  const addPhone = () => setBiz("phones", [...(content.business.phones || []), ""]);
  const removePhone = (i) => setBiz("phones", content.business.phones.filter((_, idx) => idx !== i));

  const setHour = (i, k, v) => {
    const hours = [...(content.business.hours || [])];
    hours[i] = { ...hours[i], [k]: v };
    setBiz("hours", hours);
  };
  const addHour = () => setBiz("hours", [...(content.business.hours || []), { day: "", time: "" }]);
  const removeHour = (i) => setBiz("hours", content.business.hours.filter((_, idx) => idx !== i));

  const setCat = (i, k, v) => {
    const cats = [...content.categories];
    cats[i] = { ...cats[i], [k]: v };
    setContent({ ...content, categories: cats });
  };
  const addCat = () =>
    setContent({
      ...content,
      categories: [
        ...content.categories,
        { id: `cat-${Date.now()}`, name: "New Category", description: "", badge: "", image_url: "", size: "square" },
      ],
    });
  const removeCat = (i) => setContent({ ...content, categories: content.categories.filter((_, idx) => idx !== i) });

  const setMan = (i, k, v) => {
    const m = [...content.manifesto];
    m[i] = { ...m[i], [k]: v };
    setContent({ ...content, manifesto: m });
  };
  const addMan = () =>
    setContent({ ...content, manifesto: [...content.manifesto, { number: `0${content.manifesto.length + 1}`, title: "", text: "" }] });
  const removeMan = (i) => setContent({ ...content, manifesto: content.manifesto.filter((_, idx) => idx !== i) });

  return (
    <div className="min-h-screen bg-ad-black text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-xl uppercase tracking-wide metallic-text">Content Studio</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href="/"
              target="_blank"
              rel="noreferrer"
              data-testid="view-site-btn"
              className="hidden sm:flex items-center gap-2 px-4 py-2.5 border border-white/20 text-sm uppercase tracking-wider hover:bg-white hover:text-black transition-colors"
            >
              <Eye size={15} /> View Site
            </a>
            <button
              onClick={save}
              disabled={saving}
              data-testid="save-content-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-ad-red text-white text-sm font-bold uppercase tracking-wider hover:bg-ad-redhover transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              {saving ? "Saving" : "Save"}
            </button>
            <button
              onClick={() => { logout(); navigate("/admin/login"); }}
              data-testid="logout-btn"
              className="p-2.5 border border-white/20 hover:bg-ad-red hover:border-ad-red transition-colors"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-white/10 pb-4">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t.toLowerCase().replace(/\s/g, "-")}`}
              className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition-colors ${
                tab === t ? "bg-ad-red text-white" : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
          {tab === "Business" && (
            <div className="grid md:grid-cols-2 gap-6">
              <Field label="Business Name"><input className={inputCls} value={content.business.name || ""} onChange={(e) => setBiz("name", e.target.value)} data-testid="biz-name" /></Field>
              <Field label="Name Accent"><input className={inputCls} value={content.business.name_accent || ""} onChange={(e) => setBiz("name_accent", e.target.value)} /></Field>
              <Field label="Tagline"><input className={inputCls} value={content.business.tagline || ""} onChange={(e) => setBiz("tagline", e.target.value)} /></Field>
              <Field label="Email"><input className={inputCls} value={content.business.email || ""} onChange={(e) => setBiz("email", e.target.value)} /></Field>
              <div className="md:col-span-2"><Field label="Address"><input className={inputCls} value={content.business.address || ""} onChange={(e) => setBiz("address", e.target.value)} data-testid="biz-address" /></Field></div>
              <div className="md:col-span-2"><Field label="Google Map Embed URL"><input className={inputCls} value={content.map_embed || ""} onChange={(e) => setContent({ ...content, map_embed: e.target.value })} /></Field></div>

              <div className="md:col-span-2">
                <label className={labelCls}>Phone Numbers</label>
                <div className="flex flex-col gap-3">
                  {content.business.phones?.map((p, i) => (
                    <div key={i} className="flex gap-2">
                      <input className={inputCls} value={p} onChange={(e) => setPhone(i, e.target.value)} />
                      <button onClick={() => removePhone(i)} className="px-3 border border-white/15 hover:bg-ad-red hover:border-ad-red transition-colors"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={addPhone} className="flex items-center gap-2 text-sm text-ad-red hover:text-ad-redhover w-fit"><Plus size={15} /> Add Phone</button>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className={labelCls}>Business Hours</label>
                <div className="flex flex-col gap-3">
                  {content.business.hours?.map((h, i) => (
                    <div key={i} className="flex gap-2">
                      <input className={inputCls} placeholder="Day(s)" value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} />
                      <input className={inputCls} placeholder="Time" value={h.time} onChange={(e) => setHour(i, "time", e.target.value)} />
                      <button onClick={() => removeHour(i)} className="px-3 border border-white/15 hover:bg-ad-red hover:border-ad-red transition-colors"><Trash2 size={16} /></button>
                    </div>
                  ))}
                  <button onClick={addHour} className="flex items-center gap-2 text-sm text-ad-red hover:text-ad-redhover w-fit"><Plus size={15} /> Add Hours</button>
                </div>
              </div>
            </div>
          )}

          {tab === "Hero" && (
            <div className="grid md:grid-cols-2 gap-6">
              <div className="md:col-span-2"><Field label="Logo (shown dynamically in hero)"><ImageUploader value={content.hero.logo_url} onChange={(v) => setHero("logo_url", v)} label="Logo" testid="upload-logo" /></Field></div>
              <div className="md:col-span-2"><Field label="Background Image"><ImageUploader value={content.hero.background_url} onChange={(v) => setHero("background_url", v)} label="Background" testid="upload-hero-bg" /></Field></div>
              <Field label="Eyebrow Text"><input className={inputCls} value={content.hero.eyebrow || ""} onChange={(e) => setHero("eyebrow", e.target.value)} /></Field>
              <Field label="Headline Line 1"><input className={inputCls} value={content.hero.headline_line1 || ""} onChange={(e) => setHero("headline_line1", e.target.value)} /></Field>
              <Field label="Headline Line 2 (accent)"><input className={inputCls} value={content.hero.headline_line2 || ""} onChange={(e) => setHero("headline_line2", e.target.value)} /></Field>
              <Field label="Headline Line 3"><input className={inputCls} value={content.hero.headline_line3 || ""} onChange={(e) => setHero("headline_line3", e.target.value)} /></Field>
              <div className="md:col-span-2"><Field label="Subheadline"><textarea rows={3} className={inputCls} value={content.hero.subheadline || ""} onChange={(e) => setHero("subheadline", e.target.value)} /></Field></div>
              <Field label="Primary Button Text"><input className={inputCls} value={content.hero.cta_primary || ""} onChange={(e) => setHero("cta_primary", e.target.value)} /></Field>
              <Field label="Secondary Button Text"><input className={inputCls} value={content.hero.cta_secondary || ""} onChange={(e) => setHero("cta_secondary", e.target.value)} /></Field>
            </div>
          )}

          {tab === "Collections" && (
            <div className="flex flex-col gap-5">
              {content.categories.map((cat, i) => (
                <div key={cat.id} className="bg-ad-surface border border-white/10 p-6" data-testid={`admin-cat-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display text-xl uppercase tracking-wide text-white/70">#{i + 1}</span>
                    <button onClick={() => removeCat(i)} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-ad-red transition-colors"><Trash2 size={14} /> Remove</button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Name"><input className={inputCls} value={cat.name} onChange={(e) => setCat(i, "name", e.target.value)} /></Field>
                    <Field label="Badge (optional)"><input className={inputCls} value={cat.badge || ""} onChange={(e) => setCat(i, "badge", e.target.value)} placeholder="e.g. Best Deals" /></Field>
                    <div className="md:col-span-2"><Field label="Description"><textarea rows={2} className={inputCls} value={cat.description || ""} onChange={(e) => setCat(i, "description", e.target.value)} /></Field></div>
                    <Field label="Card Size">
                      <select className={inputCls} value={cat.size || "square"} onChange={(e) => setCat(i, "size", e.target.value)}>
                        <option value="large">Large (2x2)</option>
                        <option value="tall">Tall (1x2)</option>
                        <option value="wide">Wide (2x1)</option>
                        <option value="square">Square (1x1)</option>
                      </select>
                    </Field>
                    <div><Field label="Image"><ImageUploader value={cat.image_url} onChange={(v) => setCat(i, "image_url", v)} label={cat.name} testid={`upload-cat-${i}`} /></Field></div>
                  </div>
                </div>
              ))}
              <button onClick={addCat} data-testid="add-category-btn" className="flex items-center justify-center gap-2 py-4 border border-dashed border-white/25 text-white/60 hover:border-ad-red hover:text-ad-red transition-colors uppercase tracking-wider text-sm font-bold"><Plus size={16} /> Add Collection</button>
            </div>
          )}

          {tab === "Why Us" && (
            <div className="flex flex-col gap-6">
              <div className="grid gap-4">
                <Field label="Section Heading"><input className={inputCls} value={content.about?.heading || ""} onChange={(e) => setAbout("heading", e.target.value)} /></Field>
                <Field label="Section Text"><textarea rows={3} className={inputCls} value={content.about?.text || ""} onChange={(e) => setAbout("text", e.target.value)} /></Field>
              </div>
              <div className="flex flex-col gap-4">
                {content.manifesto.map((m, i) => (
                  <div key={i} className="bg-ad-surface border border-white/10 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="font-display text-2xl text-ad-red">{m.number}</span>
                      <button onClick={() => removeMan(i)} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-ad-red transition-colors"><Trash2 size={14} /> Remove</button>
                    </div>
                    <div className="grid gap-4">
                      <div className="grid grid-cols-3 gap-4">
                        <Field label="Number"><input className={inputCls} value={m.number} onChange={(e) => setMan(i, "number", e.target.value)} /></Field>
                        <div className="col-span-2"><Field label="Title"><input className={inputCls} value={m.title} onChange={(e) => setMan(i, "title", e.target.value)} /></Field></div>
                      </div>
                      <Field label="Text"><textarea rows={2} className={inputCls} value={m.text} onChange={(e) => setMan(i, "text", e.target.value)} /></Field>
                    </div>
                  </div>
                ))}
                <button onClick={addMan} className="flex items-center justify-center gap-2 py-4 border border-dashed border-white/25 text-white/60 hover:border-ad-red hover:text-ad-red transition-colors uppercase tracking-wider text-sm font-bold"><Plus size={16} /> Add Point</button>
              </div>
            </div>
          )}

          {tab === "Products" && <AdminProducts />}

          {tab === "Videos" && <AdminVideos />}

          {tab === "Marquee" && (
            <div className="max-w-2xl">
              <Field label="Scrolling Marquee Text">
                <textarea rows={3} className={inputCls} value={content.marquee_text || ""} onChange={(e) => setContent({ ...content, marquee_text: e.target.value })} />
              </Field>
              <p className="mt-3 text-white/40 text-sm">Tip: separate items with " • " so they read well as they scroll.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
